<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <div v-if="isLoading" class="text-center text-gray-600 mt-4">
      Chargement en cours...
    </div>

    <div v-else class="mb-8">
      <!-- Toolbar avec design pattern AssetDetail -->
      <div class="anamnesis-detail-toolbar">
        <div class="anamnesis-detail-toolbar-content">
          <div class="anamnesis-detail-title-section">
            <PackageIcon class="anamnesis-detail-title-icon" />
            <div class="anamnesis-detail-title-content">
              <div class="relative">
                <input v-if="isEditingTitle" ref="titleInput" v-model="editedTitle" class="anamnesis-detail-title-input"
                  @blur="handleTitleUpdate" @keyup.enter="handleTitleUpdate" @keyup.escape="cancelTitleEdit" />
                <h1 v-else class="anamnesis-detail-title-display" @click="startTitleEdit">
                  {{ materialEntity?.appellation || 'Sans titre' }}
                </h1>
              </div>
              <div v-if="materialEntity?.identifier" class="anamnesis-detail-subtitle">
                Identifiant: {{ materialEntity.identifier }}
              </div>
            </div>
          </div>

          <!-- Actions avec gestion des permissions -->
          <div class="anamnesis-detail-actions">
            <!-- Toggle public/privé - visible pour tous, modifiable seulement par le propriétaire -->
            <button class="anamnesis-detail-status" :class="[
              materialEntity?.is_public ? 'public' : 'private',
              { disabled: !canEdit }
            ]" :disabled="!canEdit" @click="canEdit && handlePublicToggle()">
              <EyeIcon v-if="materialEntity?.is_public" class="h-4 w-4" />
              <EyeOffIcon v-else class="h-4 w-4" />
              {{ materialEntity?.is_public ? 'Public' : 'Privé' }}
            </button>

            <!-- Boutons d'action - seulement visibles pour les propriétaires/admin -->
            <template v-if="canEdit">
              <!-- Boutons d'édition -->
              <template v-if="isEditing">
                <button class="anamnesis-detail-btn-success" @click="handleSave">
                  <CheckIcon class="h-5 w-5" />
                  Valider
                </button>
                <button class="anamnesis-detail-btn-secondary" @click="handleCancel">
                  <XIcon class="h-5 w-5" />
                  Annuler
                </button>
              </template>
              <button v-else class="anamnesis-detail-btn-primary" @click="handleEdit">
                <PencilIcon class="h-5 w-5" />
              </button>

              <!-- Supprimer - seulement pour les propriétaires/admin -->
              <button v-if="canDelete" class="anamnesis-detail-btn-delete" @click="handleDelete">
                <TrashIcon class="h-5 w-5" />
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Grille principale avec fond blanc et cadres -->
      <div class="anamnesis-detail-grid">
        <!-- Colonne de gauche : Détails -->
        <div class="anamnesis-detail-section bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Informations de l'entité matérielle
          </h3>

          <!-- Type -->
          <div class="anamnesis-detail-field">
            <TagIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <div v-if="isEditing">
                <input v-model="editedMaterialEntity.type" class="anamnesis-detail-field-input"
                  placeholder="Type d'entité matérielle" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ materialEntity?.type || 'Type non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Matériau -->
          <div class="anamnesis-detail-field">
            <PackageIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Matériau</label>
              <div v-if="isEditing">
                <input v-model="editedMaterialEntity.material" class="anamnesis-detail-field-input"
                  placeholder="Matériau de composition" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ materialEntity?.material || 'Matériau non spécifié' }}
              </p>
            </div>
          </div>

          <!-- État de surface -->
          <div class="anamnesis-detail-field">
            <TagIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">État de surface</label>
              <div v-if="isEditing">
                <input v-model="editedMaterialEntity.surface_state" class="anamnesis-detail-field-input"
                  placeholder="Bon état, Usé, Restauré" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ materialEntity?.surface_state || 'État de surface non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Lien -->
          <div class="anamnesis-detail-field">
            <BookOpenIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Lien</label>
              <div v-if="isEditing">
                <input v-model="editedMaterialEntity.me_link" class="anamnesis-detail-field-input"
                  placeholder="https://example.com/objet" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                <a v-if="materialEntity?.me_link" :href="materialEntity.me_link" target="_blank" rel="noopener noreferrer"
                  class="text-blue-600 hover:text-blue-800 underline">
                  {{ materialEntity.me_link }}
                </a>
                <span v-else>Lien non spécifié</span>
              </p>
            </div>
          </div>

          <!-- Notes matériau -->
          <div class="anamnesis-detail-field">
            <BookOpenIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Notes matériau</label>
              <div v-if="isEditing">
                <textarea v-model="editedMaterialEntity.material_notes" class="anamnesis-detail-field-input"
                  placeholder="Notes sur le matériau, conservation..." rows="3" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ materialEntity?.material_notes || 'Notes matériau non spécifiées' }}
              </p>
            </div>
          </div>

          <!-- Échelle -->
          <div class="anamnesis-detail-field">
            <TagIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Échelle</label>
              <div v-if="isEditing">
                <input v-model="editedMaterialEntity.scale" class="anamnesis-detail-field-input"
                  placeholder="1:1, 1:10, etc." />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ materialEntity?.scale || 'Échelle non spécifiée' }}
              </p>
            </div>
          </div>

          <!-- Propriétaire -->
          <div class="anamnesis-detail-field">
            <UserIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Propriétaire</label>
              <div v-if="isEditing">
                <input v-model="editedMaterialEntity.me_owner" class="anamnesis-detail-field-input"
                  placeholder="Musée, Collection privée, etc." />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ materialEntity?.me_owner || 'Propriétaire non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Identifiant -->
          <div class="anamnesis-detail-field">
            <Hash class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Identifiant</label>
              <div v-if="isEditing">
                <input v-model="editedMaterialEntity.identifier" class="anamnesis-detail-field-input"
                  placeholder="Identifiant unique" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ materialEntity?.identifier || 'Identifiant non spécifié' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Colonne de droite : Provenance avec carte -->
        <div class="anamnesis-detail-metadata bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Provenance</h3>

          <!-- Sélecteur de lieu en mode édition -->
          <div v-if="isEditing" class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Lieu de provenance</label>
            <LocationSelector :user-id="authStore.user?.uuid || ''" :initial-location-id="selectedLocationId"
              @location-selected="handleLocationSelected" @location-reset="handleLocationReset" />

            <!-- Bouton pour afficher les champs de provenance détaillée -->
            <div class="mt-4">
              <button type="button" class="text-blue-500 hover:text-blue-700 text-sm font-medium"
                @click="showProvenanceFields = !showProvenanceFields">
                Préciser la provenance?
              </button>

              <!-- Champs de provenance détaillée -->
              <div v-if="showProvenanceFields" class="mt-4 space-y-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Intérieur/Extérieur</label>
                  <input v-model="editedMaterialEntity.provenance_interior_exterior"
                    class="anamnesis-detail-field-input" placeholder="Salle des Gardes, Cour intérieure" />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Zonage</label>
                  <input v-model="editedMaterialEntity.provenance_zoning" class="anamnesis-detail-field-input"
                    placeholder="Zone nord, Zone sud" />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Étage</label>
                  <input v-model="editedMaterialEntity.provenance_floor" class="anamnesis-detail-field-input"
                    placeholder="Étage 1, Rez-de-chaussée" />
                </div>



                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <input v-model="editedMaterialEntity.provenance_orientation" class="anamnesis-detail-field-input"
                    placeholder="Nord, Sud, Est, Ouest" />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nomenclature</label>
                  <input v-model="editedMaterialEntity.provenance_nomenclature" class="anamnesis-detail-field-input"
                    placeholder="Nomenclature spécifique" />
                </div>
              </div>
            </div>
          </div>

          <!-- Affichage du lieu sélectionné -->
          <div v-else class="space-y-4">
            <!-- Informations du lieu -->
            <div v-if="materialEntity?.provenance_location" class="anamnesis-detail-field">
              <MapPinIcon class="anamnesis-detail-field-icon" />
              <div class="anamnesis-detail-field-content">
                <label class="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
                <button class="anamnesis-detail-external-link" @click="handleLocationView">
                  <MapIcon class="h-4 w-4" />
                  <span>{{ getLocationDisplayName(materialEntity.provenance_location) }}</span>
                </button>
              </div>
            </div>

            <!-- Aucun lieu défini -->
            <div v-else class="anamnesis-detail-field">
              <MapPinIcon class="anamnesis-detail-field-icon text-gray-400" />
              <div class="anamnesis-detail-field-content">
                <p class="anamnesis-detail-field-empty">Aucun lieu de provenance défini</p>
              </div>
            </div>

            <!-- Mini carte si coordonnées disponibles -->
            <div v-if="materialEntity?.provenance_location && hasCoordinates(materialEntity.provenance_location)"
              class="mt-4">
              <h4 class="text-sm font-medium text-gray-700 mb-2">Localisation</h4>
              <div ref="mapContainer" class="w-full rounded-lg border border-gray-200 bg-gray-100"
                style="height: 300px; min-height: 300px;">
                <div v-if="!isMapInitialized" class="flex items-center justify-center h-full text-gray-500">
                  <div class="text-center">
                    <MapIcon class="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p class="text-sm">Chargement de la carte...</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Informations de provenance détaillée -->
            <div v-if="hasDetailedProvenance && materialEntity" class="mt-4 pt-4 border-t border-gray-200">
              <h4 class="text-sm font-medium text-gray-700 mb-3">Détails de provenance</h4>
              <div class="space-y-2 text-sm">
                <div v-if="materialEntity?.provenance_interior_exterior">
                  <span class="font-medium text-gray-600">Intérieur/Extérieur:</span>
                  <span class="ml-2 text-gray-800">{{ materialEntity.provenance_interior_exterior }}</span>
                </div>
                <div v-if="materialEntity?.provenance_zoning">
                  <span class="font-medium text-gray-600">Zonage:</span>
                  <span class="ml-2 text-gray-800">{{ materialEntity.provenance_zoning }}</span>
                </div>
                <div v-if="materialEntity?.provenance_floor">
                  <span class="font-medium text-gray-600">Étage:</span>
                  <span class="ml-2 text-gray-800">{{ materialEntity.provenance_floor }}</span>
                </div>

                <div v-if="materialEntity?.provenance_orientation">
                  <span class="font-medium text-gray-600">Orientation:</span>
                  <span class="ml-2 text-gray-800">{{ materialEntity.provenance_orientation }}</span>
                </div>
                <div v-if="materialEntity?.provenance_nomenclature">
                  <span class="font-medium text-gray-600">Nomenclature:</span>
                  <span class="ml-2 text-gray-800">{{ materialEntity.provenance_nomenclature }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Métadonnées -->
          <div class="mt-6 pt-4 border-t border-gray-200">
            <h4 class="font-medium text-gray-800 mb-3">Métadonnées</h4>

            <!-- Date de création -->
            <div class="anamnesis-detail-field">
              <ClockIcon class="anamnesis-detail-field-icon" />
              <div class="anamnesis-detail-field-content">
                <label class="block text-sm font-medium text-gray-700 mb-1">Créé le</label>
                <p class="anamnesis-detail-field-text">
                  {{ formatDate(materialEntity?.created_at) }}
                </p>
              </div>
            </div>

            <!-- Nombre de mémos -->
            <div class="anamnesis-detail-field">
              <BookOpenIcon class="anamnesis-detail-field-icon" />
              <div class="anamnesis-detail-field-content">
                <label class="block text-sm font-medium text-gray-700 mb-1">Mémos associés</label>
                <p class="anamnesis-detail-field-text">
                  {{ materialEntity?.memos?.length || 0 }} mémo(s)
                </p>
              </div>
            </div>

            <!-- Propriétaire -->
            <div class="anamnesis-detail-field">
              <UserIcon class="anamnesis-detail-field-icon" />
              <div class="anamnesis-detail-field-content">
                <label class="block text-sm font-medium text-gray-700 mb-1">Propriétaire</label>
                <p class="anamnesis-detail-field-text">
                  {{ ownerName || 'Chargement...' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Mémos avec fond blanc -->
      <div v-if="canViewSubsets" class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <h2 class="anamnesis-detail-memos-title">Mémos associés</h2>
        <div v-if="detailedMemos.length">
          <MemosSubset :memos="detailedMemos" :show-delete-button="false" @view="handleMemoView" />
        </div>
        <div v-else class="anamnesis-detail-memos-empty">
          Aucun mémo associé à cette entité matérielle
        </div>
      </div>
      
      <!-- Message d'accès restreint -->
      <div v-else class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <div class="text-center py-8">
          <EyeOffIcon class="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">Accès restreint</h3>
          <p class="text-gray-600">
            {{ !authStore.isAuthenticated ? 'Connectez-vous pour voir les mémos associés' : 'Cet asset privé ne vous appartient pas' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Bouton de retour en haut -->
    <ScrollToTop />

    <Notification ref="notification" />
    <ModalAreYouSure v-model="showDeleteModal" title="Supprimer l'entité matérielle ?"
      message="Cette action est irréversible. Êtes-vous sûr de vouloir supprimer cette entité matérielle ?"
      confirm-text="Supprimer" cancel-text="Annuler" @confirm="confirmDelete" />
    <ModalError v-model="showErrorModal" title="Entité matérielle non trouvée"
      message="Cette entité matérielle n'existe plus ou a été supprimée." action-text="Retourner à la liste"
      @action="handleErrorModalAction" />
  </div>
</template>

<script setup lang="ts">
import 'leaflet/dist/leaflet.css';
import {
    BookOpen as BookOpenIcon,
    Check as CheckIcon,
    Clock as ClockIcon,
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    Hash,
    Map as MapIcon,
    MapPin as MapPinIcon,
    Package as PackageIcon,
    Pencil as PencilIcon,
    Tag as TagIcon,
    Trash as TrashIcon,
    User as UserIcon,
    X as XIcon
} from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { deleteMaterialEntity, fetchMaterialEntityWithMemos, materialEntities, updateMaterialEntity } from '../../api/materialEntities';
import { fetchMemosUuid } from '../../api/memos';
import type { Memo } from '../../api/types';
import type { MaterialEntityWithMemos } from '../../api/types/';
import type { Location } from '../../api/types/entities/location/types';
import { useAssetsCacheStore } from '../../stores/assetsCache';
import { useAuthStore } from '../../stores/auth';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import { useUserNamesCacheStore } from '../../stores/userNamesCache';
import Breadcrumbs from '../Breadcrumbs.vue';
import Notification from '../Notification.vue';
import LocationSelector from '../selectors/LocationSelector.vue';
import MemosSubset from '../subsets/MemosSubset.vue';
import ScrollToTop from '../utils/ScrollToTop.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';
import ModalError from '../utils/modal_error.vue';

// Props
interface Props {
  uuid: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<Props>();

// Composables
const route = useRoute();
const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();
const assetsCacheStore = useAssetsCacheStore();
const userNamesCacheStore = useUserNamesCacheStore();

// État principal
const materialEntity = ref<MaterialEntityWithMemos | null>(null);
const detailedMemos = ref<Memo[]>([]);
const isLoading = ref(true);
const isEditing = ref(false);
const isEditingTitle = ref(false);
const showDeleteModal = ref(false);
const showErrorModal = ref(false);
const ownerName = ref<string>('');

// Références
const notification = ref<InstanceType<typeof Notification> | null>(null);
const titleInput = ref<HTMLInputElement | null>(null);
const mapContainer = ref<HTMLDivElement | null>(null);

// État d'édition
interface MaterialEntityEditData {
  appellation: string;
  identifier?: string;
  type?: string;
  material?: string;
  surface_state?: string;
  me_link?: string;
  material_notes?: string;
  scale?: string;
  me_owner?: string;
  is_public: boolean;
  user_id: string;
  provenance_location_id?: string;
  provenance_interior_exterior?: string;
  provenance_zoning?: string;
  provenance_floor?: string;
  provenance_orientation?: string;
  provenance_nomenclature?: string;
}

const editedMaterialEntity = ref<MaterialEntityEditData>({
  appellation: '',
  identifier: '',
  type: '',
  material: '',
  surface_state: '',
  me_link: '',
  material_notes: '',
  scale: '',
  me_owner: '',
  is_public: false,
  user_id: authStore.user?.uuid || '',
  provenance_location_id: '',
  provenance_interior_exterior: '',
  provenance_zoning: '',
  provenance_floor: '',
  provenance_orientation: '',
  provenance_nomenclature: ''
});
const editedTitle = ref('');
const selectedLocationId = ref('');
const showProvenanceFields = ref(false);

// État de la carte
let leafletMap: any = null;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let leafletMarker: any = null;
const isMapInitialized = ref(false);

// Gestion des permissions
const isMyAsset = computed((): boolean => {
  return materialEntity.value?.user_id === authStore.user?.uuid;
});

const canEdit = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canDelete = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canViewSubsets = computed((): boolean => {
  // Peut voir les subsets si :
  // 1. L'asset est public, OU
  // 2. L'utilisateur est connecté ET est propriétaire de l'asset
  return materialEntity.value?.is_public || (authStore.isAuthenticated && isMyAsset.value);
});

// Computed pour vérifier si on a des informations de provenance détaillée
const hasDetailedProvenance = computed((): boolean => {
  if (!materialEntity.value) return false;
  return !!(
    materialEntity.value.provenance_interior_exterior ||
    materialEntity.value.provenance_zoning ||
    materialEntity.value.provenance_floor ||
    materialEntity.value.provenance_orientation ||
    materialEntity.value.provenance_nomenclature
  );
});

// Récupérer l'état depuis history.state
const historyState = window.history.state;
materialEntity.value = historyState?.materialEntity || null;

onMounted(async (): Promise<void> => {
  await loadMaterialEntity();
});

const loadDetailedMemos = async (): Promise<void> => {
  if (!materialEntity.value?.memos) return;

  try {
    const memoPromises = materialEntity.value.memos.map(memo =>
      fetchMemosUuid(memo.uuid)
    );
    const memosData = await Promise.all(memoPromises);
    detailedMemos.value = memosData.map(data => data[0]);
  } catch (error) {
    console.error('Erreur lors de la récupération des détails des mémos:', error);
  }
};

const loadMaterialEntity = async (): Promise<void> => {
  if (materialEntity.value) {
    updateBreadcrumb();
    resetEditedData();
    await loadDetailedMemos();
    await loadOwnerName();
    // Attendre que le DOM soit mis à jour avant d'initialiser la carte
    await nextTick();
    setTimeout(() => initializeMap(), 200);
    isLoading.value = false;
    return;
  }

  try {
    let entityData;
    // Si l'utilisateur est connecté, utiliser l'API normale
    if (authStore.isAuthenticated) {
      entityData = await fetchMaterialEntityWithMemos(route.params.uuid as string);
    } else {
      // Sinon, utiliser l'API publique qui permet l'accès par UUID
      entityData = await materialEntities.getPublicByUuidWithMemos(route.params.uuid as string);
    }
    materialEntity.value = entityData;
    resetEditedData();
    updateBreadcrumb();
    await loadDetailedMemos();
    await loadOwnerName();
    // Attendre que le DOM soit mis à jour avant d'initialiser la carte
    await nextTick();
    setTimeout(() => initializeMap(), 200);
  } catch (error) {
    console.error('Erreur lors du chargement de l\'entité matérielle:', error);
    if (authStore.isAuthenticated) {
      await router.push('/mymaterialentities');
    } else {
      await router.push('/');
    }
  } finally {
    isLoading.value = false;
  }
};

const loadOwnerName = async (): Promise<void> => {
  if (!materialEntity.value?.user_id) {
    ownerName.value = 'Inconnu';
    return;
  }

  try {
    const name = await userNamesCacheStore.getUserNameAsync(materialEntity.value.user_id);
    ownerName.value = name || 'Utilisateur privé';
  } catch (error) {
    console.error('Erreur lors du chargement du nom du propriétaire:', error);
    ownerName.value = 'Erreur de chargement';
  }
};

const updateBreadcrumb = (): void => {
  const entityName = materialEntity.value?.appellation || 'Entité matérielle';
  const breadcrumbPath = authStore.isAuthenticated ? '/mymaterialentities' : '/';
  const breadcrumbName = authStore.isAuthenticated ? 'Mes entités matérielles' : 'Accueil';
  breadcrumbsStore.updateBreadcrumbs([
    { name: breadcrumbName, path: breadcrumbPath },
    { name: entityName, path: route.fullPath }
  ]);
};

const resetEditedData = (): void => {
  if (materialEntity.value) {
    editedMaterialEntity.value = {
      appellation: materialEntity.value.appellation,
      identifier: materialEntity.value.identifier || '',
      type: materialEntity.value.type || '',
      material: materialEntity.value.material || '',
      surface_state: materialEntity.value.surface_state || '',
      me_link: materialEntity.value.me_link || '',
      material_notes: materialEntity.value.material_notes || '',
      scale: materialEntity.value.scale || '',
      me_owner: materialEntity.value.me_owner || '',
      is_public: materialEntity.value.is_public,
      user_id: materialEntity.value.user_id,
      provenance_location_id: materialEntity.value.provenance_location?.uuid || '',
      provenance_interior_exterior: materialEntity.value.provenance_interior_exterior || '',
      provenance_zoning: materialEntity.value.provenance_zoning || '',
      provenance_floor: materialEntity.value.provenance_floor || '',
      provenance_orientation: materialEntity.value.provenance_orientation || '',
      provenance_nomenclature: materialEntity.value.provenance_nomenclature || ''
    };
    editedTitle.value = materialEntity.value.appellation;
    selectedLocationId.value = materialEntity.value.provenance_location?.uuid || '';

    // Afficher les champs de provenance détaillée s'ils contiennent des données
    showProvenanceFields.value = !!(
      materialEntity.value.provenance_interior_exterior ||
      materialEntity.value.provenance_zoning ||
      materialEntity.value.provenance_floor ||
      materialEntity.value.provenance_orientation ||
      materialEntity.value.provenance_nomenclature
    );
  }
};

// Fonctions utilitaires pour la carte et les lieux
const hasCoordinates = (location?: Location): boolean => {
  return !!(location?.latitude && location?.longitude);
};

const getLocationDisplayName = (location: Location): string => {
  if (location.site && location.place) {
    return `${location.site} - ${location.place}`;
  }
  return location.site || location.place || 'Lieu sans nom';
};

const initializeMap = async (): Promise<void> => {
  if (!materialEntity.value?.provenance_location || !hasCoordinates(materialEntity.value.provenance_location)) {
    return;
  }

  // Attendre plusieurs cycles pour s'assurer que le DOM est prêt
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 100));

  if (!mapContainer.value) {
    // Nouvelle tentative après un délai plus long
    await new Promise(resolve => setTimeout(resolve, 500));
    if (!mapContainer.value) {
      console.error('🗺️ [initializeMap] Container de carte définitivement non trouvé');
      return;
    }
  }

  try {
    // Import dynamique de Leaflet
    const L = await import('leaflet');

    // Nettoyer la carte existante
    if (leafletMap) {
      leafletMap.remove();
      leafletMap = null;
      leafletMarker = null;
      isMapInitialized.value = false;
    }

    const location = materialEntity.value.provenance_location;

    // Fixer les icônes manquantes de Leaflet
    // Solution pour résoudre les problèmes d'icônes non trouvées
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });

    // Créer la carte
    leafletMap = L.map(mapContainer.value, {
      zoomControl: true,
      attributionControl: true
    }).setView(
      [location.latitude!, location.longitude!],
      13
    );

    // Ajouter les tuiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(leafletMap);

    // Ajouter le marqueur
    leafletMarker = L.marker([location.latitude!, location.longitude!])
      .addTo(leafletMap)
      .bindPopup(getLocationDisplayName(location));

    isMapInitialized.value = true;

    // Appliquer des styles au container pour s'assurer qu'il est visible
    if (mapContainer.value) {
      mapContainer.value.style.height = '300px';
      mapContainer.value.style.minHeight = '300px';
      mapContainer.value.style.width = '100%';
      mapContainer.value.style.position = 'relative';
      mapContainer.value.style.zIndex = '30';
    }

    // Invalider la taille après plusieurs délais pour s'assurer que le DOM est prêt
    setTimeout(() => {
      if (leafletMap) {
        leafletMap.invalidateSize();
      }
    }, 100);

    setTimeout(() => {
      if (leafletMap) {
        leafletMap.invalidateSize();
      }
    }, 500);
  } catch (error) {
    console.error('🗺️ [initializeMap] Erreur lors de l\'initialisation de la carte:', error);
    isMapInitialized.value = false;
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

const handleMemoView = (memo: Memo): void => {
  router.push(`/memos/${memo.uuid}`);
};

const startTitleEdit = (): void => {
  if (!canEdit.value || !materialEntity.value) return;

  editedTitle.value = materialEntity.value.appellation || '';
  isEditingTitle.value = true;

  nextTick((): void => {
    titleInput.value?.focus();
  });
};

const cancelTitleEdit = (): void => {
  isEditingTitle.value = false;
  if (materialEntity.value) {
    editedTitle.value = materialEntity.value.appellation || '';
  }
};

const handleTitleUpdate = async (): Promise<void> => {
  if (!materialEntity.value || !canEdit.value || editedTitle.value === materialEntity.value.appellation) {
    isEditingTitle.value = false;
    return;
  }

  try {
    const updatedEntity = await updateMaterialEntity(materialEntity.value.uuid, {
      appellation: editedTitle.value.trim(),
      user_id: materialEntity.value.user_id
    });
    Object.assign(materialEntity.value, updatedEntity);

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('materialEntities', materialEntity.value);

    isEditingTitle.value = false;
    updateBreadcrumb();
    notification.value?.showNotification('Nom mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour du nom:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handleEdit = (): void => {
  if (!canEdit.value) return;
  resetEditedData();
  isEditing.value = true;
};

const handleCancel = (): void => {
  isEditing.value = false;
  isEditingTitle.value = false;
  resetEditedData();
};

const handleSave = async (): Promise<void> => {
  if (!materialEntity.value || !canEdit.value) return;

  // Créer une version propre pour l'API
  const entityToUpdate = {
    appellation: editedMaterialEntity.value.appellation,
    identifier: editedMaterialEntity.value.identifier,
    type: editedMaterialEntity.value.type,
    material: editedMaterialEntity.value.material,
    surface_state: editedMaterialEntity.value.surface_state,
    me_link: editedMaterialEntity.value.me_link,
    material_notes: editedMaterialEntity.value.material_notes,
    scale: editedMaterialEntity.value.scale,
    me_owner: editedMaterialEntity.value.me_owner,
    is_public: editedMaterialEntity.value.is_public,
    user_id: editedMaterialEntity.value.user_id,
    // Utiliser provenance au lieu de provenance_location_id pour correspondre à l'API
    provenance: selectedLocationId.value || undefined,
    provenance_interior_exterior: editedMaterialEntity.value.provenance_interior_exterior,
    provenance_zoning: editedMaterialEntity.value.provenance_zoning,
    provenance_floor: editedMaterialEntity.value.provenance_floor,
    provenance_orientation: editedMaterialEntity.value.provenance_orientation,
    provenance_nomenclature: editedMaterialEntity.value.provenance_nomenclature
  };

  try {
    // Mettre à jour l'entité
    const updatedEntity = await updateMaterialEntity(materialEntity.value.uuid, entityToUpdate);

    // Pour s'assurer d'avoir toutes les relations, rechargeons l'entité complète
    try {
      const freshEntity = await fetchMaterialEntityWithMemos(materialEntity.value.uuid);

      // Vérifier si le lieu est correctement attaché
      if (selectedLocationId.value && !freshEntity.provenance_location) {
        console.warn('⚠️ [handleSave] Lieu non présent dans l\'entité rechargée malgré provenance_location_id défini');
      }

      materialEntity.value = freshEntity;
    } catch (reloadError) {
      console.error('💾 [handleSave] Erreur lors du rechargement de l\'entité:', reloadError);
      // Même si le rechargement échoue, on utilise l'entité mise à jour retournée par l'API
      materialEntity.value = { ...updatedEntity, memos: materialEntity.value.memos || [] };
    }

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('materialEntities', materialEntity.value);

    isEditing.value = false;
    isEditingTitle.value = false;
    updateBreadcrumb();

    // Attendre que le DOM soit mis à jour avant d'initialiser la carte
    await nextTick();
    setTimeout(() => initializeMap(), 200); // Recharger la carte si le lieu a changé

    notification.value?.showNotification('Entité matérielle mise à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'entité matérielle:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handlePublicToggle = async (): Promise<void> => {
  if (!materialEntity.value?.uuid || !canEdit.value) return;

  try {
    const updatedEntity = await updateMaterialEntity(materialEntity.value.uuid, {
      is_public: !materialEntity.value.is_public,
      user_id: materialEntity.value.user_id,
      appellation: materialEntity.value.appellation
    });
    materialEntity.value = { ...updatedEntity, memos: materialEntity.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('materialEntities', materialEntity.value);

    notification.value?.showNotification('Visibilité mise à jour', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la visibilité:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour de la visibilité', 'error');
  }
};

// Gestion des lieux
const handleLocationSelected = (locationId: string): void => {
  selectedLocationId.value = locationId;
  editedMaterialEntity.value.provenance_location_id = locationId;
};

const handleLocationReset = (): void => {
  selectedLocationId.value = '';
  editedMaterialEntity.value.provenance_location_id = '';
};

const handleLocationView = (): void => {
  if (materialEntity.value?.provenance_location) {
    router.push(`/location/${materialEntity.value.provenance_location.uuid}`);
  }
};

const handleDelete = (): void => {
  if (!canDelete.value) return;
  showDeleteModal.value = true;
};

const confirmDelete = async (): Promise<void> => {
  if (!materialEntity.value?.uuid || !canDelete.value) return;

  try {
    await deleteMaterialEntity(materialEntity.value.uuid);

    // Supprimer du cache
    assetsCacheStore.removeAssetFromCache('materialEntities', materialEntity.value.uuid);

    notification.value?.showNotification('Entité matérielle supprimée avec succès', 'success');
    router.push('/mymaterialentities');
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'entité matérielle:', error);
    notification.value?.showNotification('Erreur lors de la suppression', 'error');
  }
  showDeleteModal.value = false;
};

const handleErrorModalAction = (): void => {
  router.push('/mymaterialentities');
};


</script>

<style scoped>
/* Styles pour la carte Leaflet */
:deep(.leaflet-container) {
  width: 100%;
  height: 100%;
  z-index: 10;
}

:deep(.leaflet-control-container) {
  z-index: 20;
}

:deep(.leaflet-pane) {
  z-index: 10;
}

:deep(.leaflet-tile-pane) {
  z-index: 5;
}

/* S'assurer que le container de carte a une hauteur */
.w-full {
  width: 100%;
}
</style>